/**
 * 搜索表单组件
 */

import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Row,
  Col,
  Space,
  Collapse,
} from 'antd';
import { SearchOutlined, ReloadOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useI18n } from '@/hooks/useI18n';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

export interface SearchField {
  name: string;
  label: string;
  type: 'input' | 'select' | 'date' | 'dateRange' | 'number';
  options?: Array<{ label: string; value: any }>;
  placeholder?: string;
  rules?: any[];
  span?: number;
  [key: string]: any;
}

export interface SearchFormProps {
  fields?: SearchField[];
  initialValues?: Record<string, any>;
  onSearch?: (values: Record<string, any>) => void;
  onReset?: () => void;
  layout?: 'horizontal' | 'vertical' | 'inline';
  span?: number;
  maxCount?: number;
  showCollapse?: boolean;
  submitText?: string;
  resetText?: string;
  loading?: boolean;
}

/**
 * 搜索表单组件
 */
export const SearchForm: React.FC<SearchFormProps> = ({
  fields = [],
  initialValues,
  onSearch,
  onReset,
  layout = 'horizontal',
  span = 6,
  maxCount = 3,
  showCollapse = true,
  submitText,
  resetText,
  loading = false,
}) => {
  const { t } = useI18n();
  const [form] = Form.useForm();
  const [collapsed, setCollapsed] = useState(true);

  // 同步初始值
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  // 处理搜索提交
  const handleSubmit = () => {
    form.validateFields().then(values => {
      // 处理日期范围值
      const processedValues = { ...values };
      fields?.forEach(field => {
        if (field.type === 'dateRange' && processedValues[field.name]) {
          const [start, end] = processedValues[field.name];
          processedValues[`${field.name}Start`] = start ? dayjs(start).format('YYYY-MM-DD') : undefined;
          processedValues[`${field.name}End`] = end ? dayjs(end).format('YYYY-MM-DD') : undefined;
          delete processedValues[field.name];
        }
        if (field.type === 'date' && processedValues[field.name]) {
          processedValues[field.name] = dayjs(processedValues[field.name]).format('YYYY-MM-DD');
        }
      });
      
      onSearch?.(processedValues);
    });
  };

  // 处理重置
  const handleReset = () => {
    form.resetFields();
    onReset?.();
  };

  // 渲染表单项
  const renderFormItem = (field: SearchField, index: number) => {
    const { name, label, type, options, placeholder, rules, ...rest } = field;
    
    let component: React.ReactNode;

    switch (type) {
      case 'input':
        component = (
          <Input
            data-testid={`search-input-${name}`}
            placeholder={placeholder || `${t('form.input')}${label}`}
            allowClear
            {...rest}
          />
        );
        break;

      case 'select':
        component = (
          <Select
            data-testid={`search-select-${name}`}
            placeholder={placeholder || `${t('form.select')}${label}`}
            allowClear
            {...rest}
          >
            {options?.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        );
        break;

      case 'date':
        component = (
          <DatePicker
            data-testid={`search-date-${name}`}
            placeholder={placeholder || `${t('form.date')}${label}`}
            style={{ width: '100%' }}
            {...rest}
          />
        );
        break;

      case 'dateRange':
        component = (
          <RangePicker
            data-testid={`search-daterange-${name}`}
            placeholder={[
              placeholder || `${t('form.startDate')}`,
              placeholder || `${t('form.endDate')}`
            ]}
            style={{ width: '100%' }}
            {...rest}
          />
        );
        break;

      case 'number':
        component = (
          <InputNumber
            data-testid={`search-number-${name}`}
            placeholder={placeholder || `${t('form.input')}${label}`}
            style={{ width: '100%' }}
            {...rest}
          />
        );
        break;

      default:
        component = (
          <Input
            data-testid={`search-input-${name}`}
            placeholder={placeholder || `${t('form.input')}${label}`}
            allowClear
            {...rest}
          />
        );
    }

    return (
      <Col span={field.span || span} key={name}>
        <Form.Item
          name={name}
          label={label}
          rules={rules}
        >
          {component}
        </Form.Item>
      </Col>
    );
  };

  // 显示的字段（根据折叠状态）
  const displayFields = collapsed && showCollapse && fields.length > maxCount
    ? fields.slice(0, maxCount)
    : fields;

  // 操作按钮
  const renderActions = () => (
    <Col span={span} style={{ textAlign: 'right' }}>
      <Form.Item>
        <Space>
          <Button
            data-testid="search-btn-submit"
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSubmit}
            loading={loading}
          >
            {submitText || t('common.search')}
          </Button>
          <Button
            data-testid="search-btn-reset"
            icon={<ReloadOutlined />}
            onClick={handleReset}
          >
            {resetText || t('common.reset')}
          </Button>
          {showCollapse && fields.length > maxCount && (
            <Button
              data-testid="search-btn-collapse"
              type="link"
              icon={collapsed ? <DownOutlined /> : <UpOutlined />}
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? t('common.expand') : t('common.collapse')}
            </Button>
          )}
        </Space>
      </Form.Item>
    </Col>
  );

  return (
    <Form
      form={form}
      layout={layout}
      initialValues={initialValues}
      autoComplete="off"
    >
      <Row gutter={16}>
        {displayFields.map((field, index) => renderFormItem(field, index))}
        {renderActions()}
      </Row>
    </Form>
  );
};

// 默认导出，同时支持命名导出
export default SearchForm;